"use client";
import React, { Suspense } from "react";
import { Index, useInstantSearch, Configure } from "react-instantsearch";
import { some, omit } from "lodash";

import BlogSearchFeaturedResults from "@/components/Blog/BlogSearch/BlogSearchFeaturedResults";
import BlogSearchResults from "@/components/Blog/BlogSearch/BlogSearchResults";
import BlogCurrentRefinements from "@/components/Blog/BlogSearch/BlogCurrentRefinements";
import type { BlogSidebarItemProps } from "@/components/Blog/BlogSidebar/types";
import { Container } from "@/components/Blog/Layout";
import BlogSidebarComponent from "@/components/Blog/BlogSidebar";
import {
  featuredBlogConfigureDefaultProps,
  PER_PAGE,
  BLOG_SEARCH_INDEX_NAME,
  FEATURED_SEARCH_INDEX_ID,
  scrollToResults,
} from "@/components/Blog/BlogSearch/utils";
import { VirtualMenu } from "@/utils/search/VirtualWidgets";
import { filterIn, filterNotIn, join } from "@/utils/search/typesense";
import { useResetSearchHook } from "@/components/Blog/BlogSearch/hooks";

type Props = {
  includeFilter?: string[];
  excludeFilter?: string[];
  perPage?: number;
  sidebarItems: BlogSidebarItemProps[];
};

const BlogSearch: React.FC<Props> = ({
  includeFilter = null,
  excludeFilter = null,
  perPage = PER_PAGE,
  sidebarItems,
}) => {
  const search = useInstantSearch({ catchError: false });
  const { query, page, menu } = search.indexUiState;
  const isSearch = some([query, page, menu?.tag, menu?.category]);
  const shouldHaveFeatured = !isSearch;
  const featuredUIDs: string[] =
    search.scopedResults
      .find(({ indexId }) => indexId === "blogPostFeatured")
      ?.results?.hits.filter(({ uid }) => !!uid)
      .map(({ uid }) => uid) ?? [];

  const filters = join([
    ...(includeFilter ? [filterIn("tag", includeFilter)] : []),
    ...(excludeFilter ? [filterNotIn("tag", excludeFilter)] : []),
    ...(shouldHaveFeatured && featuredUIDs.length > 0
      ? [filterNotIn("uid", featuredUIDs)]
      : []),
  ]);

  // tools for reset search if navigation used
  const resetSearch = React.useCallback(() => {
    search.setIndexUiState((previousIndexUiState) =>
      omit(previousIndexUiState, ["menu", "page", "query"]),
    );
  }, []);
  useResetSearchHook({ callback: resetSearch });

  return (
    <Container>
      <div className="relative">
        {shouldHaveFeatured && (
          <div className="lg:pr-[300px]">
            <Index
              indexName={BLOG_SEARCH_INDEX_NAME}
              indexId={FEATURED_SEARCH_INDEX_ID}
            >
              <Configure {...featuredBlogConfigureDefaultProps} />
              <BlogSearchFeaturedResults
                onTagClick={(tag) => {
                  search.setIndexUiState((previousIndexUiState) => ({
                    ...previousIndexUiState,
                    menu: {
                      ...previousIndexUiState.menu,
                      tag,
                    },
                  }));
                  scrollToResults();
                }}
              />
            </Index>
          </div>
        )}
        <Configure
          hitsPerPage={!isSearch ? perPage - featuredUIDs.length : perPage}
          filters={filters}
        />
        <Suspense>
          <BlogCurrentRefinements />
        </Suspense>
        <VirtualMenu attribute="tag" />
        <BlogSearchResults />
        {shouldHaveFeatured && (
          <div className="lg:absolute lg:right-0 lg:top-0 lg:w-[306px]">
            <BlogSidebarComponent
              items={sidebarItems}
              classNames={{
                desktop: "bg-transparent pt-0 lg:pr-0",
              }}
            />
          </div>
        )}
      </div>
    </Container>
  );
};

export default BlogSearch;
